import {
	logbookNavbar,
	type NavbarRendererProps,
	useHasPermission,
	useUserContext,
} from "@dexteel/mesf-core";
import { NavbarMenuBar, NavbarMenuConfig } from "./components/NavbarMenuBar";
import {
	COOLING_BED_PRODUCTION_LABEL,
	MILL_1,
	MILL_2,
	STACKER_PRODUCTION_LABEL,
} from "./mill-config/mill-config";
import {
	adjustmentsManagerPermission,
	adjustmentsReportPermission,
	routeAdjustmentManager,
	routeAdjustmentReport,
} from "./routes/adjustment.routes";
import {
	routeCalendarYear,
	routeCalendarYearM1,
	routeCalendarYearM2,
} from "./routes/calendar-year.routes";
import {
	calendarPermission,
	calendarSalesPermission,
	routeCalendar,
	routeCalendarM1,
	routeCalendarM2,
} from "./routes/calendar.routes";
import {
	catalogsArborsPermission,
	catalogsCarriersPermission,
	catalogsCassetteChocksPermission,
	catalogsCassettesPermission,
	catalogsChocksPermission,
	catalogsDonutsPermission,
	catalogsEdgerRollsPermission,
	catalogsGuidesPermission,
	catalogsUniversalRollsPermission,
	routeArbor,
	routeCarrier,
	routeCassette,
	routeCassetteChock,
	routeChock,
	routeDonut,
	routeEdgerRoll,
	routeGuide,
	routeUniversalRoll,
} from "./routes/catalog.routes";
import {
	delaysManagerPermission,
	delaysParetoPermission,
	delaysReportPermission,
	delaysSankeyPermission,
	routeDelaysManager,
	routeDelaysPareto,
	routeDelaysReport,
	routeDelaysSankey,
} from "./routes/delay.routes";
import {
	incentiveDashboardPermission,
	incentiveManagerPermission,
	routeIncentive,
	routeIncentiveManager,
} from "./routes/incentive.routes";
import {
	inspectionsManagerPermission,
	inspectionsReportPermission,
	routeInspectionsManager,
	routeInspectionsReport,
} from "./routes/inspection.routes";
import {
	labChemistriesReportPermission,
	labChemistryRangesPermission,
	labDashboardPermission,
	labSupplierHeatsPermission,
	labTensileRequirementsPermission,
	labTestsBySectionPermission,
	routeChemistriesReport,
	routeLabChemistryRanges,
	routeLabDashboard,
	routeLabSupplierHeats,
	routeLabTensileRequirements,
	routeLabTestsBySection,
} from "./routes/lab.routes";
import {
	m1GanttPermission,
	m1IncentiveDashboardPermission,
	m1IncentiveManagerPermission,
	m1MillOverviewPermission,
	m1MillSchedulePermission,
	m1ShiftReportPermission,
	routeM1Gantt,
	routeM1Incentive,
	routeM1IncentiveManager,
	routeM1Overview,
	routeM1Schedule,
	routeM1ShiftReport,
} from "./routes/m1.routes";
import {
	millCarrierStatusPermission,
	millCobblesManagerPermission,
	millGanttPermission,
	millNavbarPermission,
	millOverview2Permission,
	millOverviewPermission,
	millRejectedManagerPermission,
	millSchedulePermission,
	routeCarrierAssembly,
	routeCobblesManager,
	routeMillGantt,
	routeMillOverview,
	routeMillOverview2,
	routeMillSchedule,
	routeRejectedManager,
} from "./routes/mill.routes";
import {
	m1TestShackManagerPermission,
	m1TestShackPermission,
	m1TestShackReportPermission,
	productsTestShackManagerPermission,
	productsTestShackPermission,
	productsTestShackReportPermission,
	routeM1TestShack,
	routeM1TestShackManager,
	routeM1TestShackReport,
	routeProductsTestShack,
	routeProductsTestShackManager,
	routeProductsTestShackReport,
} from "./routes/products.routes";
import { partsPermission, routePartsManager } from "./routes/report.routes";
import {
	rollshopNotificationsPermission,
	rollshopRollsetsPermission,
	routeBrokenRolls,
	routeRollsets,
	routeRollshopNotifications,
} from "./routes/rollshop.routes";
import { routeSales, salesPermission } from "./routes/sales.routes";
import {
	routeShiftReport,
	shiftReportPermission,
} from "./routes/shift_report.routes";
import {
	millStackerProductionPermission,
	routeStackerProduction,
} from "./routes/stacker-pulpit.routes";
import {
	gasEntryPermission,
	routeGasEntry,
	routeUtilityConsumption,
	routeUtilityEngineering,
	routeUtilityManagement,
	utilityConsumptionPermission,
	utilityEngineeringPermission,
	utilityManagementPermission,
	utilityNavbarPermission,
} from "./routes/utility.routes";

interface AreaAwareNavbarMenuConfig extends NavbarMenuConfig {
	defaultAreaAssetId?: number[];
}

const menuConfigData: AreaAwareNavbarMenuConfig[] = [
	{
		id: "rollshop",
		label: "Rollshop",
		items: [
			{
				label: "Rollsets",
				to: routeRollsets,
				permission: rollshopRollsetsPermission,
			},
			{
				label: "Notifications",
				to: routeRollshopNotifications,
				permission: rollshopNotificationsPermission,
			},
			{
				label: "Broken Rolls",
				to: routeBrokenRolls,
			},
		],
		defaultAreaAssetId: [12],
		permission: "ui.navbar.rollshop",
	},
	{
		id: "mill",
		label: "Mill",
		items: [
			{
				label: "Carrier Status",
				to: routeCarrierAssembly,
				permission: millCarrierStatusPermission,
			},
			{
				label: "Overview",
				to: routeMillOverview,
				permission: millOverviewPermission,
			},
			{
				label: "Overview 2",
				to: routeMillOverview2,
				permission: millOverview2Permission,
			},
			{
				label: "Schedule",
				to: routeMillSchedule,
				permission: millSchedulePermission,
			},
			{
				label: "Cobbles Manager",
				to: routeCobblesManager,
				permission: millCobblesManagerPermission,
			},
			{
				label: "Rejected Manager",
				to: routeRejectedManager,
				permission: millRejectedManagerPermission,
			},
			{
				label: "Gantt",
				to: routeMillGantt,
				permission: millGanttPermission,
			},
			{
				label: "Incentive Dashboard",
				to: routeIncentive,
				permission: incentiveDashboardPermission,
			},
			{
				label: "Incentive Manager",
				to: routeIncentiveManager,
				permission: incentiveManagerPermission,
			},
			{
				label: STACKER_PRODUCTION_LABEL,
				to: routeStackerProduction,
				permission: millStackerProductionPermission,
			},
			{
				label: "Shift Report",
				to: routeShiftReport,
				permission: shiftReportPermission,
			},
		],
		defaultAreaAssetId: [12],
		permission: millNavbarPermission,
	},
	{
		id: "m1-mill",
		label: "Mill",
		items: [
			{
				label: "Overview",
				to: routeM1Overview,
				permission: m1MillOverviewPermission,
			},
			{
				label: "Schedule",
				to: routeM1Schedule,
				permission: m1MillSchedulePermission,
			},
			{
				label: "Incentive Dashboard",
				to: routeM1Incentive,
				permission: m1IncentiveDashboardPermission,
			},
			{
				label: "Incentive Manager",
				to: routeM1IncentiveManager,
				permission: m1IncentiveManagerPermission,
			},
			{
				label: COOLING_BED_PRODUCTION_LABEL,
				to: routeStackerProduction,
				permission: "ui.navbar.m1.mill.stacker.production",
			},
			{
				label: "Shift Report",
				to: routeM1ShiftReport,
				permission: m1ShiftReportPermission,
			},
			{
				label: "Gantt",
				to: routeM1Gantt,
				permission: m1GanttPermission,
			},
		],
		defaultAreaAssetId: [MILL_1.id],
		permission: "ui.navbar.m1.mill",
	},
	{
		id: "utilities",
		label: "Utilities",
		items: [
			{
				label: "Billing",
				permission: utilityManagementPermission,
				items: [
					{ label: "Water", to: `${routeUtilityManagement}?utility=water` },
					{ label: "Gas", to: `${routeUtilityManagement}?utility=gas` },
				],
			},
			{
				label: "Gas Reading",
				to: routeGasEntry,
				permission: gasEntryPermission,
			},
			{
				label: "Report",
				permission: utilityConsumptionPermission,
				items: [
					{ label: "Water", to: `${routeUtilityConsumption}?utility=water` },
					{ label: "Gas", to: `${routeUtilityConsumption}?utility=gas` },
				],
			},
			{
				label: "Engineering",
				permission: utilityEngineeringPermission,
				items: [
					{ label: "Water", to: `${routeUtilityEngineering}?utility=water` },
				],
			},
		],
		defaultAreaAssetId: [MILL_1.id, 12],
		permission: utilityNavbarPermission,
	},
	{
		id: "yearview-sales",
		label: "Year View",
		items: [
			{
				label: "M1 Year View",
				to: routeCalendarYearM1,
			},
			{
				label: "M2 Year View",
				to: routeCalendarYearM2,
			},
		],
		permission: "ui.navbar.yearview.sales",
	},
	{
		id: "calendar-sales",
		label: "Calendar",
		items: [
			{
				label: "M1 Calendar",
				to: routeCalendarM1,
			},
			{
				label: "M2 Calendar",
				to: routeCalendarM2,
			},
		],
		permission: calendarSalesPermission,
	},
	{
		id: "calendar",
		label: "Calendar",
		items: [
			{
				label: "Calendar",
				to: routeCalendar,
			},
			{
				label: "Year View",
				to: routeCalendarYear,
			},
		],
		defaultAreaAssetId: [MILL_1.id, 12],
		permission: calendarPermission,
	},
	{
		id: "sales",
		label: "Sales",
		to: routeSales,
		items: [],
		defaultAreaAssetId: [MILL_1.id, 12],
		permission: salesPermission,
	},
	{
		id: "adjustments",
		label: "Adjustments",
		items: [
			{
				label: "Manager",
				to: routeAdjustmentManager,
				permission: adjustmentsManagerPermission,
			},
			{
				label: "Report",
				to: routeAdjustmentReport,
				permission: adjustmentsReportPermission,
			},
		],
		defaultAreaAssetId: [12],
		permission: "ui.navbar.adjustments",
	},
	{
		id: "delays",
		label: "Delays",
		items: [
			{
				label: "Manager",
				to: routeDelaysManager,
				permission: delaysManagerPermission,
			},
			{
				label: "Report",
				to: routeDelaysReport,
				permission: delaysReportPermission,
			},
			{
				label: "Pareto",
				to: routeDelaysPareto,
				permission: delaysParetoPermission,
			},
			{
				label: "Sankey",
				to: routeDelaysSankey,
				permission: delaysSankeyPermission,
			},
		],
		defaultAreaAssetId: [MILL_1.id, 12],
		permission: "ui.navbar.delays",
	},
	{
		id: "products",
		label: "Products",
		items: [
			{
				label: "Section Profile — Test Shack",
				to: routeProductsTestShack,
				permission: productsTestShackPermission,
			},
			{
				label: "Test Shack Manager",
				to: routeProductsTestShackManager,
				permission: productsTestShackManagerPermission,
			},
			{
				label: "Test Shack Report",
				to: routeProductsTestShackReport,
				permission: productsTestShackReportPermission,
			},
		],
		defaultAreaAssetId: [MILL_2.id],
		permission: "ui.navbar.products",
	},
	{
		id: "lab",
		label: "Lab",
		items: [
			{
				label: "Chemistries Report",
				to: routeChemistriesReport,
				permission: labChemistriesReportPermission,
			},
			//   {
			//     label: "Chemistries Dashboard",
			//     to: routeLabChemistries,
			//     permission: labChemistriesPermission,
			//   },
			{
				label: "Lab Dashboard",
				to: routeLabDashboard,
				permission: labDashboardPermission,
			},
			{
				label: "Supplier Heats",
				to: routeLabSupplierHeats,
				permission: labSupplierHeatsPermission,
			},
			{
				label: "Tests by Section",
				to: routeLabTestsBySection,
				permission: labTestsBySectionPermission,
			},
			{
				label: "Chemistry Ranges",
				to: routeLabChemistryRanges,
				permission: labChemistryRangesPermission,
			},
			{
				label: "Tensile Requirements",
				to: routeLabTensileRequirements,
				permission: labTensileRequirementsPermission,
			},
		],
		defaultAreaAssetId: [MILL_1.id, 12],
		permission: "ui.navbar.lab",
	},
	{
		id: "inspections",
		label: "Inspections",
		items: [
			{
				label: "Manager",
				to: routeInspectionsManager,
				permission: inspectionsManagerPermission,
			},
			{
				label: "Report",
				to: routeInspectionsReport,
				permission: inspectionsReportPermission,
			},
		],
		defaultAreaAssetId: [MILL_1.id],
		permission: "ui.navbar.inspections",
	},
	{
		id: "m1-products",
		label: "Products",
		items: [
			{
				label: "Section Profile — Test Shack",
				to: routeM1TestShack,
				permission: m1TestShackPermission,
			},
			{
				label: "Test Shack Manager",
				to: routeM1TestShackManager,
				permission: m1TestShackManagerPermission,
			},
			{
				label: "Test Shack Report",
				to: routeM1TestShackReport,
				permission: m1TestShackReportPermission,
			},
		],
		defaultAreaAssetId: [MILL_1.id],
		permission: "ui.navbar.products",
	},
	{
		...logbookNavbar[0],
		defaultAreaAssetId: [MILL_1.id, 12],
		permission: "ui.navbar.logbook",
	},
	{
		id: "catalogs",
		label: "Catalogs",
		items: [
			{
				label: "Arbors",
				to: routeArbor,
				permission: catalogsArborsPermission,
			},
			{
				label: "Donuts",
				to: routeDonut,
				permission: catalogsDonutsPermission,
			},
			{
				label: "Universal Rolls",
				to: routeUniversalRoll,
				permission: catalogsUniversalRollsPermission,
			},
			{
				label: "Edger Rolls",
				to: routeEdgerRoll,
				permission: catalogsEdgerRollsPermission,
			},
			{
				label: "Carriers",
				to: routeCarrier,
				permission: catalogsCarriersPermission,
			},
			{
				label: "Cassettes",
				to: routeCassette,
				permission: catalogsCassettesPermission,
			},
			{
				label: "Chocks",
				to: routeChock,
				permission: catalogsChocksPermission,
			},
			{
				label: "Cassette Chocks",
				to: routeCassetteChock,
				permission: catalogsCassetteChocksPermission,
			},
			{
				label: "Guides",
				to: routeGuide,
				permission: catalogsGuidesPermission,
			},
			{
				label: "Parts Manager",
				to: routePartsManager,
				permission: partsPermission,
			},
		],
		defaultAreaAssetId: [12],
		permission: "ui.navbar.catalogs",
	},
];

const CustomNavbar = (props: NavbarRendererProps = {}) => {
	const hasPermissionTo = useHasPermission();

	const {
		state: { defaultAreaId },
	} = useUserContext();

	const menus = menuConfigData.filter((menu) => {
		const isAreaAllowed =
			!menu.defaultAreaAssetId ||
			menu.defaultAreaAssetId.includes(Number(defaultAreaId));
		const hasPermission = menu.permission
			? hasPermissionTo(menu.permission)
			: true;
		return isAreaAllowed && hasPermission;
	});

	return <NavbarMenuBar menus={menus} {...props} />;
};

export { CustomNavbar };
